// src/services/questionnaireService.js

class QuestionnaireService {
    constructor() {
        this.basePath = '/api/core-questions';
        this.debug = process.env.NODE_ENV === 'development';
    }

    async validateResponses(organizationId, responses) {
        try {
            // Get all questions first
            const questions = await this.getQuestions();
            
            // Early return if no questions
            if (!questions || !Array.isArray(questions)) {
                throw new Error('Failed to fetch questions for validation');
            }

            // Filter required questions
            const requiredQuestions = questions.filter(q => 
                q.required === true || q.required === 1
            );

            // Track missing responses
            const missingRequired = requiredQuestions.filter(question => {
                // Handle case where responses is undefined
                if (!responses) return true;
                
                // Get response value, handling both direct values and response objects
                const response = responses[question.id];
                if (!response) return true;
                
                const value = response.response_value !== undefined ? 
                    response.response_value : response;

                // Check for empty values based on type
                if (value === null || value === undefined || value === '') {
                    return true;
                }

                // Type-specific validation
                switch (question.question_type) {
                    case 'multiple_choice':
                        // Handle array or JSON string
                        try {
                            const choices = Array.isArray(value) ? value :
                                typeof value === 'string' ? JSON.parse(value) : [value];
                            return choices.length === 0;
                        } catch (e) {
                            return true;
                        }

                    case 'range':
                        try {
                            const range = typeof value === 'object' ? value :
                                JSON.parse(value);
                            return !range.min || !range.max;
                        } catch (e) {
                            return true;
                        }

                    case 'tristate_boolean':
                        return !['YES', 'NO', 'DONT_KNOW'].includes(String(value).toUpperCase());

                    case 'boolean':
                        return value !== true && value !== false && 
                               value !== 'true' && value !== 'false';

                    default:
                        return false;
                }
            });

            return {
                isValid: missingRequired.length === 0,
                missingQuestions: missingRequired.map(q => ({
                    id: q.id,
                    text: q.question_text,
                    type: q.question_type
                }))
            };

        } catch (error) {
            console.error('[QuestionnaireService] Error validating responses:', error);
            throw error;
        }
    }


    async getQuestions() {
        const response = await fetch(this.basePath, {
            credentials: 'include',
            headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch questions: ${response.statusText}`);
        }
        return response.json();
    }

    async saveReviewResponse(questionId, organizationId, value, questionType) {
        if (!questionId || !organizationId) {
            throw new Error('Question ID and Organization ID are required');
        }
    
        try {
            const preparedValue = this.prepareResponseValue(value, questionType);
            
            // First save as team response
            const teamResponse = await this.saveTeamResponse(
                questionId,
                organizationId,
                value,
                questionType
            );
    
            // If user is admin, save to organization responses
            if (teamResponse.is_admin_response) {
                const response = await fetch(`${this.basePath}/responses/organization`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        question_id: questionId,
                        organization_id: organizationId,
                        response_value: preparedValue,
                        locked_to_user_id: teamResponse.user_id
                    })
                });
    
                if (!response.ok) {
                    throw new Error(`Failed to save organization response: ${response.statusText}`);
                }
            }
    
            return teamResponse;
        } catch (error) {
            console.error('[QuestionnaireService] Error saving review response:', error);
            throw error;
        }
    }
    
    async saveAllReviewResponses(organizationId, responses, isAdmin = false) {
        if (!organizationId || !responses) {
            throw new Error('Organization ID and responses are required');
        }
    
        try {
            console.log('[QuestionnaireService] Saving review responses:', {
                organizationId,
                responseCount: Object.keys(responses).length,
                isAdmin
            });
    
            // Save user responses first
            const userResult = await fetch(`${this.basePath}/responses/bulk`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    organization_id: organizationId,
                    responses: Object.entries(responses).map(([questionId, response]) => ({
                        question_id: questionId,
                        response_value: response.response_value
                    }))
                })
            });
    
            if (!userResult.ok) {
                throw new Error('Failed to save user responses');
            }
    
            // If admin, also save organization responses
            if (isAdmin) {
                console.log('[QuestionnaireService] Saving org responses for admin');
                
                const orgResponse = await fetch(`${this.basePath}/responses/organization/bulk`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        organization_id: organizationId,
                        responses: responses
                    })
                });
    
                if (!orgResponse.ok) {
                    const errorText = await orgResponse.text();
                    console.error('Organization response save failed:', errorText);
                    throw new Error('Failed to save organization responses');
                }
            }
    
            const result = await userResult.json();
    
            if (this.debug) {
                console.log('[QuestionnaireService] Saved review responses:', result);
            }
    
            return result;
        } catch (error) {
            console.error('[QuestionnaireService] Error saving review responses:', error);
            throw error;
        }
    }
   // In questionnaireService.js

async getResponses(organizationId) {
    if (!organizationId) {
        throw new Error('Organization ID is required');
    }

    try {
        // Always fetch user-specific responses
        const userResponse = await fetch(`${this.basePath}/responses/${organizationId}/team`, {
            credentials: 'include',
            headers: { 'Accept': 'application/json' }
        });

        if (!userResponse.ok) {
            throw new Error(`Failed to fetch user responses: ${userResponse.statusText}`);
        }

        const userResponses = await userResponse.json();
        
        // Convert array of responses to object keyed by question_id
        // Maintain both user response and org response in structure
        const responseMap = userResponses.reduce((acc, response) => {
            acc[response.question_id] = {
                ...response,
                response_value: this.prepareResponseValue(
                    response.response_value, 
                    response.question_type
                ),
                // Preserve organization response if it exists
                organization_response: response.organization_response ? {
                    ...response.organization_response,
                    response_value: this.prepareResponseValue(
                        response.organization_response.response_value,
                        response.question_type
                    )
                } : null
            };
            return acc;
        }, {});

        if (this.debug) {
            console.log('[QuestionnaireService] Fetched responses:', responseMap);
        }

        return responseMap;
    } catch (error) {
        console.error('[QuestionnaireService] Error fetching responses:', error);
        throw error;
    }
}
    

    // Add new method for team responses
    async saveTeamResponse(questionId, organizationId, value, questionType) {
        if (!questionId || !organizationId) {
            throw new Error('Question ID and Organization ID are required');
        }

        const preparedValue = this.prepareResponseValue(value, questionType);

        try {
            console.log('[QuestionnaireService] Saving team response:', {
                questionId,
                organizationId,
                value: preparedValue,
                questionType
            });

            const response = await fetch(`${this.basePath}/responses/team`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    question_id: questionId,
                    organization_id: organizationId,
                    response_value: preparedValue
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to save team response: ${response.statusText}`);
            }

            const result = await response.json();

            if (this.debug) {
                console.log('[QuestionnaireService] Saved team response:', result);
            }

            return result;
        } catch (error) {
            console.error('[QuestionnaireService] Error saving team response:', error);
            throw error;
        }
    }
    async saveResponse(questionId, organizationId, value) {
        if (!questionId || !organizationId) {
            throw new Error('Question ID and Organization ID are required');
        }

        const preparedValue = this.prepareResponseValue(value);

        try {
            const response = await fetch(`${this.basePath}/responses`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    question_id: questionId,
                    organization_id: organizationId,
                    response_value: preparedValue
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to save response: ${response.statusText}`);
            }

            const result = await response.json();

            if (this.debug) {
                console.log('[QuestionnaireService] Saved response:', {
                    questionId,
                    value: preparedValue,
                    result
                });
            }

            return result;
        } catch (error) {
            console.error('[QuestionnaireService] Error saving response:', error);
            throw error;
        }
    }

    async getVisibilityRules(organizationId) {
        if (!organizationId) {
            throw new Error('Organization ID is required');
        }

        try {
            const response = await fetch(`${this.basePath}/visibility/${organizationId}`, {
                credentials: 'include',
                headers: { 'Accept': 'application/json' }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch visibility rules: ${response.statusText}`);
            }

            const rules = await response.json();

            if (this.debug) {
                console.log('[QuestionnaireService] Visibility rules:', rules);
            }

            return rules;
        } catch (error) {
            console.error('[QuestionnaireService] Error fetching visibility rules:', error);
            throw error;
        }
    }

    async getQuestionComments(questionId, organizationId) {
        const response = await fetch(
            `${this.basePath}/comments/${organizationId}/${questionId}`,
            {
                credentials: 'include',
                headers: { 'Accept': 'application/json' }
            }
        );

        if (!response.ok) {
            throw new Error('Failed to fetch comments');
        }

        return response.json();
    }

    async addQuestionComment(questionId, organizationId, text) {
        const response = await fetch(`${this.basePath}/comments`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                question_id: questionId,
                organization_id: organizationId,
                comment_text: text
            })
        });

        if (!response.ok) {
            throw new Error('Failed to add comment');
        }

        return response.json();
    }

// Update markComplete function to send responses in correct format
async markComplete(organizationId) {
    if (!organizationId) {
        throw new Error('Organization ID is required');
    }

    try {
        // Get current responses before marking complete
        const responses = await this.getResponses(organizationId);

        // Validate responses
        const validation = await this.validateResponses(organizationId, responses);
        if (!validation.isValid) {
            throw new Error(`Required questions not answered: ${validation.missingQuestions.length} remaining`);
        }

        // Mark as complete - send responses in format expected by endpoint
        const completeResponse = await fetch(`${this.basePath}/complete/${organizationId}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                responses: Object.entries(responses).reduce((acc, [questionId, response]) => {
                    acc[questionId] = {
                        question_id: questionId,
                        response_value: response.response_value
                    };
                    return acc;
                }, {})
            })
        });

        if (!completeResponse.ok) {
            const errorData = await completeResponse.json();
            throw new Error(errorData.error || 'Failed to mark questionnaire complete');
        }

        const result = await completeResponse.json();

        if (this.debug) {
            console.log('[QuestionnaireService] Marked questionnaire complete:', result);
        }

        return result;
    } catch (error) {
        console.error('[QuestionnaireService] Error completing questionnaire:', error);
        throw error;
    }
}

    // Helper method to prepare response values for saving
    prepareResponseValue(value, questionType) {
        if (value === null || value === undefined) {
            return null;
        }

        // If value is already an object with response_value, extract it
        if (typeof value === 'object' && value !== null && 'response_value' in value) {
            value = value.response_value;
        }

        switch (questionType) {
            case 'tristate_boolean':
                // Extract just the value for tristate
                return value.toString().toUpperCase();

            case 'multiple_choice':
                // Ensure array format
                if (Array.isArray(value)) {
                    return value;
                }
                if (typeof value === 'string') {
                    try {
                        return JSON.parse(value);
                    } catch {
                        return [value];
                    }
                }
                return [value];

            case 'range':
                // Ensure range format
                if (typeof value === 'string') {
                    try {
                        return JSON.parse(value);
                    } catch {
                        return value;
                    }
                }
                return value;

            case 'boolean':
                return value.toString();

            default:
                return value.toString();
        }
    }

    async saveBulkResponses(organizationId, responses) {
        if (!organizationId) {
            throw new Error('Organization ID is required');
        }

        try {
            // Get questions for type information
            const questions = await this.getQuestions();
            const questionMap = questions.reduce((acc, q) => {
                acc[q.id] = q;
                return acc;
            }, {});

            const preparedResponses = Object.entries(responses).map(([questionId, value]) => {
                const questionType = questionMap[questionId]?.question_type;
                return {
                    question_id: questionId,
                    response_value: this.prepareResponseValue(value, questionType)
                };
            });

            const response = await fetch(`${this.basePath}/responses/bulk`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    organization_id: organizationId,
                    responses: preparedResponses
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(`Failed to save bulk responses: ${error.error || response.statusText}`);
            }

            const result = await response.json();

            if (this.debug) {
                console.log('[QuestionnaireService] Saved bulk responses:', result);
            }

            return result;
        } catch (error) {
            console.error('[QuestionnaireService] Error saving bulk responses:', error);
            throw error;
        }
    }

    // Validation helper
// In questionnaireService.js

async validateResponses(organizationId, responses) {
    try {
        // Get all questions first
        const questions = await this.getQuestions();
        
        if (!questions || !Array.isArray(questions)) {
            throw new Error('Failed to fetch questions for validation');
        }

        // Filter required questions
        const requiredQuestions = questions.filter(q => 
            q.required === true || q.required === 1
        );

        // Track missing required responses
        const missingRequired = requiredQuestions.filter(question => {
            // Handle case where responses is undefined
            if (!responses) return true;
            
            // Get response value, handling both direct values and response objects
            const response = responses[question.id];
            if (!response) return true;
            
            const value = response.response_value !== undefined ? 
                response.response_value : response;

            // Check for empty values based on type
            if (value === null || value === undefined || value === '') {
                return true;
            }

            // Type-specific validation for required questions
            switch (question.question_type) {
                case 'multiple_choice':
                    try {
                        const choices = Array.isArray(value) ? value :
                            typeof value === 'string' ? JSON.parse(value) : [value];
                        return choices.length === 0;
                    } catch (e) {
                        return true;
                    }

                case 'range':
                    try {
                        const range = typeof value === 'object' ? value :
                            JSON.parse(value);
                        // Only validate if not skipped
                        if (range.skipped) return true;
                        return !range.min || !range.max;
                    } catch (e) {
                        return true;
                    }

                case 'tristate_boolean':
                    return !['YES', 'NO', 'DONT_KNOW'].includes(String(value).toUpperCase());

                case 'boolean':
                    return value !== true && value !== false && 
                           value !== 'true' && value !== 'false';

                case 'text':
                    return typeof value !== 'string' || value.trim() === '';

                default:
                    // For any unhandled types, consider the question answered if there's any value
                    return !value && value !== false;
            }
        });

        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
            console.log('Validation results:', {
                totalQuestions: questions.length,
                requiredQuestions: requiredQuestions.length,
                missingQuestions: missingRequired.map(q => ({
                    id: q.id,
                    text: q.question_text,
                    type: q.question_type
                }))
            });
        }

        return {
            isValid: missingRequired.length === 0,
            missingQuestions: missingRequired.map(q => ({
                id: q.id,
                text: q.question_text,
                type: q.question_type
            }))
        };

    } catch (error) {
        console.error('[QuestionnaireService] Error validating responses:', error);
        throw error;
    }
}

    // Visibility helper methods
    shouldShowFeature(featureRef, visibilityRules) {
        if (!visibilityRules?.features) return true;
        return visibilityRules.features[featureRef] !== false;
    }

    shouldShowSection(sectionName, visibilityRules) {
        if (!visibilityRules?.sections) return true;
        return visibilityRules.sections[sectionName] !== false;
    }

    filterFeatures(features, visibilityRules) {
        if (!visibilityRules?.features) return features;
        return features.filter(feature => 
            this.shouldShowFeature(feature.reference, visibilityRules)
        );
    }

    filterSections(sections, visibilityRules) {
        if (!visibilityRules?.sections) return sections;
        return sections.filter(section => 
            this.shouldShowSection(section.name, visibilityRules)
        );
    }
}

export default new QuestionnaireService();